import React, { useState, useContext, useEffect, useCallback } from 'react';
import { AuthContext } from '../context/AuthContext';
import api from '../api';
import styles from '../styles/UserSettings.module.css';
import { TIMEZONES } from '../utils/constants';
import { validateEmail, validatePhone } from '../utils/validation';
import EditableField from '../components/EditableField';
import DiscordVerification from '../components/DiscordVerification';
import SMSVerification from '../components/SMSVerification';
import VersionFooter from '../components/VersionFooter';

const REMINDER_TIMES = [
  { value: 'week_start', label: 'Week Start' },
  { value: 'friday_night', label: 'Friday Nights' },
  { value: 'gameday_morning', label: 'Gameday Mornings' },
  { value: 'last_chance', label: 'Last Chance' }
];

const PICKSLIP_BEHAVIORS = [
  { value: 'peek', label: 'Peek' },
  { value: 'open', label: 'Open' },
  { value: 'none', label: 'None' }
];

const REMINDER_METHODS = [
  { value: 'email', label: 'Email' },
  { value: 'sms', label: 'SMS' },
  { value: 'discord', label: 'Discord' }
];

const UserSettingsPage = () => {
  const { user, updateUser, mergeUser } = useContext(AuthContext);

  const [formData, setFormData] = useState({
    username: '',
    newPassword: '',
    confirmPassword: '',
    discord_username: '',
    email: '',
    phone: '',
    timezone: 'America/New_York',
    pickslip_behavior: 'peek',
    reminder_preferences: [],
    reminder_times: [],
    default_bye_visibility: false,
    user_notes: ''
  });

  const [successMessage, setSuccessMessage] = useState('');

  // Update formData when user data loads
  useEffect(() => {
    console.log('UserSettingsPage useEffect triggered, user:', user);
    if (user && user.id && user.email) { // Only update if we have complete user data
      console.log('Populating formData with user data:', user);
      setFormData(prevFormData => {
        // Only update if the user data has actually changed
        if (prevFormData.username === user.username &&
            prevFormData.email === user.email &&
            prevFormData.discord_username === user.discord_username) {
          console.log('User data unchanged, skipping formData update');
          return prevFormData;
        }

        return {
          username: user.username || '',
          newPassword: '',
          confirmPassword: '',
          discord_username: user.discord_username || '',
          email: user.email || '',
          phone: user.phone || '',
          timezone: user.timezone || 'America/New_York',
          pickslip_behavior: user.pickslip_behavior || 'peek',
          reminder_preferences: (() => {
            if (!user.reminder_preferences) return [];
            if (typeof user.reminder_preferences === 'string') {
              try {
                return JSON.parse(user.reminder_preferences);
              } catch (error) {
                console.warn('Failed to parse reminder_preferences:', error);
                return [];
              }
            }
            return Array.isArray(user.reminder_preferences) ? user.reminder_preferences : [];
          })(),
          reminder_times: (() => {
            if (!user.reminder_times) return [];
            if (typeof user.reminder_times === 'string') {
              try {
                return JSON.parse(user.reminder_times);
              } catch (error) {
                console.warn('Failed to parse reminder_times:', error);
                return [];
              }
            }
            return Array.isArray(user.reminder_times) ? user.reminder_times : [];
          })(),
          default_bye_visibility: user.default_bye_visibility || false,
          user_notes: user.user_notes || ''
        };
      });
    }
  }, [user]);


  // Direct API call for toggles (no global state update)
  const debouncedApiCall = useCallback(() => {
    let timeoutId;
    return (name, value) => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(async () => {
        try {
          // Call API directly without updating global state
          await api.put('/users/settings', { [name]: value });
          setSuccessMessage(`${name} updated successfully`);
          setTimeout(() => setSuccessMessage(''), 3000);
        } catch (error) {
          console.error('Error updating field:', error);
          setSuccessMessage(`Error updating ${name}`);
          setTimeout(() => setSuccessMessage(''), 3000);
        }
      }, 500); // 500ms delay
    };
  }, [])();

  // Handle toggle fields (checkboxes) - no global state update
  const handleToggleSave = (name, value) => {
    // Update local state immediately for instant UI feedback
    setFormData(prev => ({
      ...prev,
      [name]: (name === 'reminder_times' || name === 'reminder_preferences')
        ? (typeof value === 'string' ? JSON.parse(value) : value)
        : value
    }));

    // Debounce the API call (no global state update)
    debouncedApiCall(name, value);
  };

  // Handle regular fields (text inputs) - with global state update
  const handleFieldSave = async (name, value) => {
    try {
      // If Discord username is being changed, reset verification and disable Discord reminders
      if (name === 'discord_username' && value !== user.discord_username) {
        // Reset Discord verification fields and disable Discord reminders
        const updates = {
          [name]: value,
          discord_verified_at: null,
          discord_verification_code: null,
          discord_verification_expires_at: null
        };

        // Remove Discord from reminder preferences if it's currently enabled
        const currentPreferences = Array.isArray(formData.reminder_preferences) ? formData.reminder_preferences : [];
        if (currentPreferences.includes('discord')) {
          const newPreferences = currentPreferences.filter(pref => pref !== 'discord');
          updates.reminder_preferences = JSON.stringify(newPreferences);

          // Update local state immediately
          setFormData(prev => ({
            ...prev,
            reminder_preferences: newPreferences
          }));
        }

        await updateUser(updates);
        setSuccessMessage('Discord username updated. Please re-verify your Discord account to enable reminders.');
      } else if (name === 'phone' && value !== user.phone) {
        const updates = {
          [name]: value,
          phone_verified_at: null,
          sms_verification_code: null,
          sms_verification_expires_at: null,
          sms_reminders_enabled: 0
        };

        const currentPreferences = Array.isArray(formData.reminder_preferences) ? formData.reminder_preferences : [];
        if (currentPreferences.includes('sms')) {
          const newPreferences = currentPreferences.filter(pref => pref !== 'sms');
          updates.reminder_preferences = JSON.stringify(newPreferences);

          setFormData(prev => ({
            ...prev,
            reminder_preferences: newPreferences
          }));
        }

        await updateUser(updates);
        setSuccessMessage('Phone number updated. Please re-verify your phone to enable SMS reminders.');
      } else {
        await updateUser({ [name]: value });
        setSuccessMessage(`${name} updated successfully`);
      }
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (error) {
      console.error('Error updating field:', error);
      setSuccessMessage(`Error updating ${name}`);
      setTimeout(() => setSuccessMessage(''), 3000);
    }
  };

  const handleVerificationUpdate = (updates) => {
    if (updates && typeof updates === 'object') {
      mergeUser(updates);
    }
  };

  // Safety check for user object
  if (!user || !user.id) {
    return <div>Loading user data...</div>;
  }

  return (
    <div className={styles.settingsContainer}>
      <h2 className={styles.title}>User Settings</h2>

      {successMessage && <div className={styles.successMessage}>{successMessage}</div>}

      <section className={styles.section}>
        <h3>Site tour</h3>
        <p>Replay the first-login walkthrough of Weekly Picks, locking, League Stats, and Discord.</p>
        <button
          type="button"
          className={styles.button}
          onClick={async () => {
            try {
              await api.updateOnboardingTour('pending');
              mergeUser({ onboarding_tour_status: 'pending' });
              setSuccessMessage('Tour will start now.');
            } catch (err) {
              setSuccessMessage('');
            }
          }}
        >
          Replay site tour
        </button>
      </section>

      {/* Profile Section */}
      <section className={styles.section}>
        <h3>Profile Information</h3>

        <EditableField
          label="Username"
          value={user?.username || ''}
          name="username"
          onSave={handleFieldSave}
          validation={(value) => !value.trim() ? 'Username is required' : null}
        />

        <EditableField
          label="Email Address"
          value={user?.email || ''}
          name="email"
          type="email"
          onSave={handleFieldSave}
          validation={validateEmail}
        />

        <div className={styles.profileFieldGroup}>
          <EditableField
            label="Discord Username"
            value={user?.discord_username || ''}
            name="discord_username"
            onSave={handleFieldSave}
            grouped
            helperText="Save your username here, then verify below for Discord pick reminders"
          />

          <DiscordVerification user={user} onUpdate={handleVerificationUpdate} variant="inline" />
        </div>

        <div className={styles.profileFieldGroup}>
          <EditableField
            label="Phone Number"
            value={user?.phone || ''}
            name="phone"
            type="tel"
            onSave={handleFieldSave}
            validation={validatePhone}
            grouped
            helperText="Save your number here, then verify below for SMS reminders and password reset"
          />

          <SMSVerification user={user} onUpdate={handleVerificationUpdate} variant="inline" />
        </div>
      </section>

      {/* Password Section */}
      <section className={styles.section}>
        <h3>Change Password</h3>
        <form onSubmit={(e) => e.preventDefault()}>
          <EditableField
            key="change-password"
            label="New Password"
            value=""
            name="new-password"
            type="password"
            onSave={async (name, value) => {
              try {
                await api.resetPassword(value);
                setSuccessMessage('Password updated successfully');
                setTimeout(() => setSuccessMessage(''), 3000);
              } catch (error) {
                setSuccessMessage(`Failed to update password: ${error.message}`);
              }
            }}
            validation={(value) => value.length < 6 ? 'Password must be at least 6 characters' : null}
          />
        </form>
      </section>

      {/* Game Preferences Section */}
      <section className={styles.section}>
        <h3>Game Preferences</h3>

                {/* Pickslip Behavior */}
                <div className={styles.fieldGroup}>
          <label htmlFor="pickslip-behavior" className={styles.fieldLabel}>
            Pickslip Behavior
          </label>
          <select
            id="pickslip-behavior"
            className={styles.select}
            value={formData.pickslip_behavior}
            onChange={(e) => handleFieldSave('pickslip_behavior', e.target.value)}
          >
            {PICKSLIP_BEHAVIORS.map(({ value, label }) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
          </select>
          <span className={styles.helperText}>
            Controls how the pickslip responds after watching a pick:
            <br />• Peek (Default): Briefly shows the pickslip to help new users notice where their watches are stored
            <br />• Open: Keeps the pickslip open after watching, making it easier to lock picks immediately
            <br />• None: Pickslip stays hidden, ideal for watching multiple games before locking picks
          </span>
        </div>

        {/* Bye Week Visibility */}
        <div className={styles.fieldGroup}>
          <label htmlFor="bye-visibility" className={styles.fieldLabel}>
            Default Bye Week Visibility
          </label>
          <label className={styles.toggleSwitch} aria-label="Toggle bye week visibility">
            <input
              id="bye-visibility"
              type="checkbox"
              checked={formData.default_bye_visibility}
              onChange={(e) => handleToggleSave('default_bye_visibility', e.target.checked)}
            />
            <span className={styles.slider}></span>
          </label>
        </div>

        {/* Timezone Selection */}
        <div className={styles.fieldGroup}>
          <label htmlFor="timezone" className={styles.fieldLabel}>
            Timezone
          </label>
          <select
            id="timezone"
            className={styles.select}
            value={formData.timezone}
            onChange={(e) => handleFieldSave('timezone', e.target.value)}
          >
            {TIMEZONES.map(({ value, label }) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
          </select>
        </div>
      </section>

      {/* Reminder Methods Section */}
      <section className={styles.section}>
        <div className={styles.sectionHeader}>
          <h3>Weekly Lock Reminders</h3>
        </div>
        <div className={styles.reminderOptions}>
          {REMINDER_METHODS.map(({ value, label }) => {
            const isDiscord = value === 'discord';
            const isEmail = value === 'email';
            const isSms = value === 'sms';
            const isDiscordVerified = user?.discord_verified_at && user.discord_verified_at !== 'undefined';
            const isPhoneVerified = user?.phone_verified_at && user.phone_verified_at !== 'undefined';
            const isEnabled = Array.isArray(formData.reminder_preferences) && formData.reminder_preferences.includes(value);
            const isDisabled = (isDiscord && !isDiscordVerified) || (isSms && !isPhoneVerified) || isEmail;

            return (
              <div key={value} className={styles.fieldGroup}>
                <label htmlFor={`reminder-${value}`} className={styles.fieldLabel}>
                  {isDiscord && <span className={styles.newFeature}>NEW</span>}
                  {label} Notifications
                  {isEmail && <span className={styles.comingSoon}>(Coming Soon)</span>}
                </label>
                <label
                  className={styles.toggleSwitch}
                  aria-label={`Toggle ${label.toLowerCase()} notifications`}
                  title={
                    isDiscord && !isDiscordVerified ?
                      'You must verify your Discord account before you can enable Discord reminders. Complete verification in Profile above.' :
                    isSms && !isPhoneVerified ?
                      'You must verify your phone number before you can enable SMS reminders. Complete verification in Profile above.' :
                    isEmail ?
                      'Email reminders are coming soon!' :
                    undefined
                  }
                >
                  <input
                    id={`reminder-${value}`}
                    type="checkbox"
                    checked={isEnabled}
                    disabled={isDisabled}
                    onChange={(e) => {
                      if (isDisabled) {
                        return; // Don't allow toggling if disabled
                      }

                      const currentPreferences = Array.isArray(formData.reminder_preferences) ? formData.reminder_preferences : [];
                      let newPreferences;

                      if (e.target.checked) {
                        newPreferences = [...currentPreferences, value];
                      } else {
                        newPreferences = currentPreferences.filter(pref => pref !== value);
                      }

                      handleToggleSave('reminder_preferences', JSON.stringify(newPreferences));
                    }}
                  />
                  <span className={styles.slider}></span>
                </label>
                {isDiscord && !isDiscordVerified && (
                  <div className={styles.verificationRequired}>
                    <em>Discord verification required</em>
                  </div>
                )}
                {isSms && !isPhoneVerified && (
                  <div className={styles.verificationRequired}>
                    <em>Phone verification required</em>
                  </div>
                )}
              </div>
            );
          })}

          <div className={styles.reminderTimes}>
            <label htmlFor="reminder-times-group" className={styles.fieldLabel}>
              When to Send Reminders
            </label>
            <div id="reminder-times-group" className={styles.checkboxGroup}>
              {REMINDER_TIMES.map(({ value, label }) => (
                <label key={value} htmlFor={`reminder-time-${value}`} className={styles.checkboxLabel}>
                  <input
                    id={`reminder-time-${value}`}
                    type="checkbox"
                    checked={formData.reminder_times.includes(value)}
                    onChange={(e) => {
                      const currentTimes = Array.isArray(formData.reminder_times) ? formData.reminder_times : [];
                      let newTimes;

                      if (e.target.checked) {
                        newTimes = [...currentTimes, value];
                      } else {
                        newTimes = currentTimes.filter(time => time !== value);
                      }

                      handleToggleSave('reminder_times', JSON.stringify(newTimes));
                    }}
                  />
                  {label}
                </label>
              ))}
            </div>
            <span className={styles.helperText}>
              Reminders will only be sent if you haven't made your locks for the week.
              "Week Start" sends Monday 10AM, "Gameday Mornings" sends at 10AM on game days, "Friday Nights" sends at 9PM, "Last Chance" sends 1 hour before the last lockable game.
            </span>
          </div>
        </div>
      </section>

      {/* Notes Section */}
      <section className={styles.section}>
        <h3>Personal Notes</h3>
        <EditableField
          label="Notes"
          value={formData.user_notes}
          name="user_notes"
          type="textarea"
          onSave={handleFieldSave}
          helperText="This is a notes field for adding any information you'd like. If you choose to input details such as your preferred payment service (e.g., Venmo, CashApp, Zelle, PayPal, GPay) and the associated username, please note that this app is a social tool and does not condone or facilitate gambling in any form. All notes are encrypted in the database for your privacy."
        />
      </section>

      <VersionFooter />
    </div>
  );
};

export default UserSettingsPage;
